import { usePortalAccess } from '../../components/PortalAccess';
import { useEffect, useState, useCallback, useRef } from 'react'
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import {
  getPlatform, getMonthlyStats, getPosts, syncVK, updatePost, vkDiagnose,
  getVKKpi, getVKDailyStats, getVKSubscribersDaily, getVKPostEr, getVKMoM,
  getCategories,
} from '../../utils/smmApi'
import { KPICard } from '../../components/smm/KPICard'
import { PeriodSelector } from '../../components/smm/PeriodSelector'
import { MetricTooltip } from '../../components/smm/MetricTooltip'
import { formatNumber, formatFull, formatDate, erClass } from '../../utils/smmFormat'
import styles from './SmmVK.module.css'

const PERIOD_OPTIONS = [
  { label: '7д', value: 7 },
  { label: '30д', value: 30 },
  { label: 'Пред. месяц', value: 'prev_month' },
  { label: 'Квартал', value: 90 },
  { label: 'Даты', value: 'custom' },
]

const MOM_TOOLTIPS = {
  'Подписчики': { desc: 'Число подписчиков на конец месяца (последний день в DailyStats)', src: 'API' },
  'Изменение (прирост)': { desc: 'Подписавшиеся − Отписавшиеся за месяц', src: 'Расчёт' },
  'Подписались': { desc: 'Новые подписчики за месяц', src: 'API' },
  'Отписались': { desc: 'Отписавшиеся за месяц', src: 'API' },
  'Кол-во постов': { desc: 'Количество опубликованных постов за месяц', src: 'Расчёт' },
  'Суммарный охват постов': { desc: 'Сумма охватов всех постов за месяц', src: 'API' },
  'Средний охват на пост': { desc: 'Суммарный охват / кол-во постов', src: 'Расчёт' },
  'Просмотры (ср.)': { desc: 'Среднее кол-во просмотров на пост (views_1 из wall.get)', src: 'API' },
  'Лайки': { desc: 'Суммарные лайки за месяц (из DailyStats)', src: 'API' },
  'Комментарии': { desc: 'Суммарные комментарии за месяц (из DailyStats)', src: 'API' },
  'Репосты': { desc: 'Суммарные репосты постов за месяц (из PostMetrics)', src: 'API' },
  'ER': { desc: 'Средний ER постов за месяц: среднее из (лайки+комм+реп)/подписчики×100 по каждому посту', src: 'Расчёт' },
  'ERReach': { desc: '(лайки + комментарии + репосты) / суммарный охват × 100', src: 'Расчёт' },
  'Reach Rate': { desc: 'Суммарный охват постов / подписчики × 100', src: 'Расчёт' },
  'Скрытые/жалобы': { desc: 'Скрытые записи + жалобы за месяц (из DailyStats)', src: 'API' },
  'Охват пользователей': { desc: 'Уникальные пользователи, которые видели контент сообщества. Вводится вручную. Данные доступны только за месяц.', src: 'Вручную' },
  'Посещения профиля': { desc: 'Сколько раз пользователи заходили в сообщество. Вводится вручную. Данные доступны только за месяц.', src: 'Вручную' },
  'Просмотры контента': { desc: 'Сколько раз пользователи смотрели контент: посты, клипы, видео, истории. Вводится вручную. Данные доступны только за месяц.', src: 'Вручную' },
  'Сообщения группе': { desc: 'Количество пользователей, написавших сообщения в группу. Вводится вручную. Данные доступны только за месяц.', src: 'Вручную' },
  'Написали впервые': { desc: 'Пользователи, написавшие в группу впервые. Вводится вручную. Данные доступны только за месяц.', src: 'Вручную' },
}

const FORMAT_OPTIONS = ['Все', 'video', 'photo', 'text']
const FORMAT_LABELS = { video: 'Видео', photo: 'Фото', text: 'Текст' }
const FORMAT_COLORS = { video: '#6366f1', photo: '#22c55e', text: '#94a3b8' }
const PIE_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899']

function getPrevMonthRange() {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const year = first.getFullYear()
  const month = first.getMonth() + 1
  const lastDate = new Date(year, month, 0).getDate()
  const pad = (n) => String(n).padStart(2, '0')
  return {
    date_from: `${year}-${pad(month)}-01`,
    date_to: `${year}-${pad(month)}-${pad(lastDate)}`,
  }
}

function getDates(period, dateFrom, dateTo) {
  if (period === 'custom' && dateFrom && dateTo) return { date_from: dateFrom, date_to: dateTo }
  if (period === 'prev_month') return getPrevMonthRange()
  if (typeof period === 'number') return { days: period }
  return {}
}

function calcDelta(curr, prev) {
  if (curr === null || prev === null || prev === 0) return null
  return Math.round((curr - prev) / Math.abs(prev) * 100)
}

export default function SmmVK() {
  const { permissions } = usePortalAccess();
  const canManage = permissions['smm.manage'] === true;
  const [platform, setPlatform] = useState(null)
  const [stats, setStats] = useState([])
  const [kpi, setKpi] = useState(null)
  const [dailyStats, setDailyStats] = useState([])
  const [subscribersHistory, setSubscribersHistory] = useState([])
  const [postEr, setPostEr] = useState([])
  const [momData, setMomData] = useState(null)
  const momTableRef = useRef(null)
  const [categories, setCategories] = useState([])

  useEffect(() => {
    if (!momData?.months?.length) return undefined
    const frame = requestAnimationFrame(() => {
      if (momTableRef.current) momTableRef.current.scrollLeft = momTableRef.current.scrollWidth
    })
    return () => cancelAnimationFrame(frame)
  }, [momData])
  const [posts, setPosts] = useState([])
  const [postsTotal, setPostsTotal] = useState(0)
  const [postsPage, setPostsPage] = useState(1)
  const [postsExpanded, setPostsExpanded] = useState(false)
  const [postsFormat, setPostsFormat] = useState('Все')
  const [postsCategoryId, setPostsCategoryId] = useState(null)
  const [postsSortBy, setPostsSortBy] = useState('published_at')
  const [postsSortDir, setPostsSortDir] = useState('desc')
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState(() => {
    try {
      const s = localStorage.getItem('global_period')
      if (!s) return 30
      const v = JSON.parse(s)
      return typeof v === 'number' || typeof v === 'string' ? v : 30
    } catch { return 30 }
  })
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [syncGroupId, setSyncGroupId] = useState(() =>
    (() => { try { const s = localStorage.getItem('vk_saved_group_ids'); return s ? JSON.parse(s)[0] ?? '' : '' } catch { return '' } })()
  )
  const [savedGroupIds, setSavedGroupIds] = useState(() => {
    try { const s = localStorage.getItem('vk_saved_group_ids'); return s ? JSON.parse(s) : [] } catch { return [] }
  })
  const [syncMsg, setSyncMsg] = useState('')
  const [diagnosing, setDiagnosing] = useState(false)
  const [diagnoseResult, setDiagnoseResult] = useState(null)

  useEffect(() => {
    Promise.allSettled([getPlatform('vk'), getMonthlyStats('vk'), getCategories()])
      .then(([platformRes, statsRes, categoriesRes]) => {
        if (platformRes.status === 'fulfilled') setPlatform(platformRes.value)
        else console.error(platformRes.reason)
        if (statsRes.status === 'fulfilled') setStats(statsRes.value)
        else console.error(statsRes.reason)
        if (categoriesRes.status === 'fulfilled') setCategories(categoriesRes.value)
        else console.error(categoriesRes.reason)
      })
      .finally(() => setLoading(false))
  }, [])

  const loadPeriodData = useCallback(() => {
    const params = getDates(period, dateFrom, dateTo)
    if (period === 'custom' && (!dateFrom || !dateTo)) return

    const daysOrRange = typeof period === 'number' ? { days: period } : params
    Promise.allSettled([
      getVKKpi(params),
      getVKDailyStats(daysOrRange),
      getVKSubscribersDaily(daysOrRange),
      getVKPostEr(10),
      getVKMoM(6),
    ])
      .then(([kRes, dRes, shRes, peRes, mRes]) => {
        if (kRes.status === 'fulfilled') setKpi(kRes.value)
        else console.error(kRes.reason)
        if (dRes.status === 'fulfilled') setDailyStats(dRes.value)
        else console.error(dRes.reason)
        if (shRes.status === 'fulfilled') setSubscribersHistory(shRes.value)
        else console.error(shRes.reason)
        if (peRes.status === 'fulfilled') setPostEr(peRes.value)
        else console.error(peRes.reason)
        if (mRes.status === 'fulfilled') setMomData(mRes.value)
        else console.error(mRes.reason)
      })
  }, [period, dateFrom, dateTo])

  useEffect(() => { loadPeriodData() }, [loadPeriodData])

  // Reset page + expand when filters change
  useEffect(() => {
    setPostsPage(1)
    setPostsExpanded(false)
  }, [period, postsFormat, postsCategoryId, postsSortBy, postsSortDir])

  const perPage = postsExpanded ? 50 : 10

  useEffect(() => {
    const params = getDates(period, dateFrom, dateTo)
    getPosts({
      platform_slug: 'vk',
      page: postsPage,
      per_page: perPage,
      ...(postsFormat !== 'Все' ? { format: postsFormat } : {}),
      ...(postsCategoryId ? { category_id: postsCategoryId } : {}),
      sort_by: postsSortBy,
      sort_dir: postsSortDir,
      ...params,
    })
      .then(data => { setPosts(data.items); setPostsTotal(data.total) })
      .catch(console.error)
  }, [postsPage, perPage, period, dateFrom, dateTo, postsFormat, postsCategoryId, postsSortBy, postsSortDir])

  const handleSort = (col) => {
    if (postsSortBy === col) {
      setPostsSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setPostsSortBy(col)
      setPostsSortDir('desc')
    }
  }

  const handleCategoryChange = async (postId, categoryId) => {
    try {
      await updatePost(postId, { category_id: categoryId })
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, category_id: categoryId } : p))
    } catch (e) {
      console.error(e)
    }
  }

  const handleDiagnose = async () => {
    setDiagnosing(true); setDiagnoseResult(null)
    try {
      const res = await vkDiagnose()
      setDiagnoseResult(res)
    } catch (e) {
      setDiagnoseResult({ error: e?.response?.data?.detail || e?.message })
    } finally {
      setDiagnosing(false)
    }
  }

  const handleSync = async () => {
    if (!syncGroupId.trim()) return
    setSyncing(true); setSyncMsg('')
    try {
      const gid = syncGroupId.trim()
      await syncVK(gid)
      setSyncMsg('Синхронизация запущена (~60 сек). Обновите страницу после завершения.')
      setSavedGroupIds(prev => {
        const updated = [gid, ...prev.filter(x => x !== gid)].slice(0, 10)
        try { localStorage.setItem('vk_saved_group_ids', JSON.stringify(updated)) } catch {}
        return updated
      })
    } catch (e) {
      setSyncMsg('Ошибка: ' + (e?.response?.data?.detail || e?.message || 'неизвестна'))
    } finally {
      setSyncing(false)
    }
  }

  const catCounts = categories.reduce((acc, cat) => {
    const count = posts.filter(p => p.category_id === cat.id).length
    if (count > 0) acc.push({ name: cat.name, value: count, color: cat.color })
    return acc
  }, [])
  const uncategorized = posts.filter(p => !p.category_id).length
  if (uncategorized > 0) catCounts.push({ name: 'Без рубрики', value: uncategorized, color: '#475569' })

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><span className="spinner" /></div>

  if (!platform?.is_active) {
    return (
      <div style={{ padding: 40, maxWidth: 480, margin: '0 auto' }}>
        <h2 style={{ marginBottom: 8 }}>ВКонтакте не подключён</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
          Добавьте токен сообщества в{' '}
          <a href="/settings" style={{ color: 'var(--accent)' }}>Настройках</a>,
          затем введите ID группы и запустите первую синхронизацию.
        </p>
        {canManage && <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input list="vk-group-ids-main" className="form-input" placeholder="ID группы (число)" value={syncGroupId}
            onChange={e => setSyncGroupId(e.target.value)} style={{ flex: 1 }} />
          <datalist id="vk-group-ids-main">
            {savedGroupIds.map(id => <option key={id} value={id} />)}
          </datalist>
          <button className="btn btn-primary" onClick={handleSync} disabled={syncing || !syncGroupId.trim()}>
            {syncing ? 'Запуск...' : 'Синхронизировать'}
          </button>
        </div>}
        {syncMsg && <p style={{ fontSize: 13, color: syncMsg.startsWith('Ошибка') ? 'var(--er-low)' : 'var(--er-high)' }}>{syncMsg}</p>}
      </div>
    )
  }

  const totalPages = Math.ceil(postsTotal / perPage)
  const d = kpi?.delta ?? {}

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>ВКонтакте</h1>
          <p className={styles.subtitle}>
            {platform.last_sync_at ? `Синхронизировано: ${formatDate(platform.last_sync_at)}` : 'Синхронизация не выполнялась'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <PeriodSelector options={PERIOD_OPTIONS} value={period} onChange={v => {
            setPeriod(v)
            try { localStorage.setItem('global_period', JSON.stringify(v)) } catch {}
          }} />
          {period === 'custom' && (
            <>
              <input type="date" className="form-input" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)} style={{ fontSize: 13, width: 140 }} />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
              <input type="date" className="form-input" value={dateTo}
                onChange={e => setDateTo(e.target.value)} style={{ fontSize: 13, width: 140 }} />
            </>
          )}
          {canManage && (
            <>
              <input list="vk-group-ids-header" className="form-input" placeholder="ID группы" value={syncGroupId}
                onChange={e => setSyncGroupId(e.target.value)} style={{ width: 130, fontSize: 13 }} />
              <datalist id="vk-group-ids-header">
                {savedGroupIds.map(id => <option key={id} value={id} />)}
              </datalist>
              <button className="btn btn-secondary btn-sm" onClick={handleSync} disabled={syncing || !syncGroupId.trim()}>
                {syncing ? '...' : 'Синк'}
              </button>
            </>
          )}
        </div>
      </div>
      {syncMsg && (
        <p style={{ fontSize: 13, color: syncMsg.startsWith('Ошибка') ? 'var(--er-low)' : 'var(--er-high)' }}>{syncMsg}</p>
      )}

      {/* Diagnose panel */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <button className="btn btn-ghost btn-sm" onClick={handleDiagnose} disabled={diagnosing}
          style={{ fontSize: 12, opacity: 0.6 }}>
          {diagnosing ? 'Проверка...' : 'Диагностика API'}
        </button>
        {diagnoseResult && (
          <pre style={{
            fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-secondary)',
            border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px',
            maxHeight: 260, overflow: 'auto', flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {JSON.stringify(diagnoseResult, null, 2)}
          </pre>
        )}
      </div>

      {/* KPI Cards */}
      {kpi && (
        <div className={styles.kpiGrid}>
          <KPICard label="Подписчики" value={formatFull(kpi.subscribers)}
            subvalue={kpi.subscriber_change !== null && kpi.subscriber_change !== undefined
              ? `${kpi.subscriber_change >= 0 ? '+' : ''}${formatNumber(kpi.subscriber_change)}`
              : undefined}
            subvalueColor={kpi.subscriber_change > 0 ? 'positive' : kpi.subscriber_change < 0 ? 'negative' : 'neutral'}
            tooltip="Количество подписчиков на конец периода" source="API" />
          <KPICard label="Вовлечённость (ER)" value={kpi.avg_er !== null ? `${kpi.avg_er}%` : '—'}
            tooltip="Средний ER постов: (лайки + комментарии + репосты) / подписчики × 100. Среднее по всем постам за период." source="Расчёт"
            delta={d.avg_er} />
          <KPICard label="Reach Rate" value={kpi.reach_rate !== null ? `${kpi.reach_rate}%` : '—'}
            tooltip="Охват / подписчики × 100%" source="Расчёт"
            delta={d.total_reach} />
          <KPICard label="Охват суммарный" value={formatNumber(kpi.total_reach)}
            tooltip="Сумма охватов постов за период" source="API"
            delta={d.total_reach} />
          <KPICard label="Ср. охват поста" value={formatNumber(kpi.avg_post_reach)}
            tooltip="Охват / количество постов" source="Расчёт" />
          <KPICard label="Лайки" value={formatNumber(kpi.total_likes)}
            tooltip="Суммарные лайки за период" source="API"
            delta={d.total_likes} />
          <KPICard label="Репосты" value={formatNumber(kpi.total_reposts)}
            tooltip="Суммарные репосты за период" source="API"
            delta={d.total_reposts} />
          <KPICard label="Комментарии" value={formatNumber(kpi.total_comments)}
            tooltip="Суммарные комментарии за период" source="API"
            delta={d.total_comments} />
          <KPICard label="Просмотры" value={formatNumber(kpi.total_views)}
            tooltip="Суммарные просмотры постов за период" source="API"
            delta={d.total_views} />
          <KPICard label="Кол-во постов" value={formatNumber(kpi.posts_count)}
            tooltip="Количество постов за период" source="Расчёт"
            delta={d.posts_count} />
        </div>
      )}

      {/* KPI Cards — row 2: community stats */}
      {kpi && (kpi.profile_visits > 0 || kpi.content_views > 0 || kpi.messages > 0 || kpi.content_reach > 0) && (
        <div className={styles.kpiGrid}>
          <KPICard label="Охват пользователей" value={formatNumber(kpi.content_reach)}
            tooltip="Уникальные пользователи, которые видели контент сообщества. Вводится вручную. Данные только за месяц."
            source="Вручную" delta={d.content_reach} />
          <KPICard label="Посещения профиля" value={formatNumber(kpi.profile_visits)}
            tooltip="Сколько раз пользователи заходили в сообщество. Вводится вручную. Данные только за месяц."
            source="Вручную" delta={d.profile_visits} />
          <KPICard label="Просмотры контента" value={formatNumber(kpi.content_views)}
            tooltip="Сколько раз пользователи смотрели контент: посты, клипы, видео, истории. Вводится вручную. Данные только за месяц."
            source="Вручную" delta={d.content_views} />
          <KPICard label="Сообщения группе" value={formatNumber(kpi.messages)}
            subvalue={kpi.new_messages > 0 ? `${formatNumber(kpi.new_messages)} впервые` : undefined}
            subvalueColor="neutral"
            tooltip="Количество пользователей, написавших сообщения в группу. Вводится вручную. Данные только за месяц."
            source="Вручную" delta={d.messages} />
        </div>
      )}

      {/* Charts row 1 */}
      <div className={styles.chartsRow}>
        <div className={styles.chartCardWide}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Подписчики</span>
            <span className={styles.chartHint}>динамика по дням · подп./отписки — правая ось</span>
          </div>
          {subscribersHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={subscribersHistory} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis yAxisId="left" width={42} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} domain={['auto', 'auto']}
                  tickFormatter={(v) => v.toLocaleString('ru-RU')} />
                <YAxis yAxisId="right" width={24} orientation="right" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }}
                  formatter={(v, name) => [
                    name === 'Подписчики' ? v.toLocaleString('ru-RU') : v,
                    name,
                  ]} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="left" type="monotone" dataKey="subscribers" stroke="#6366f1" dot={false} strokeWidth={2} name="Подписчики" />
                <Line yAxisId="right" type="monotone" dataKey="subscribed" stroke="#22c55e" dot={false} strokeWidth={1.5} name="Подписались" connectNulls />
                <Line yAxisId="right" type="monotone" dataKey="unsubscribed" stroke="#ef4444" dot={false} strokeWidth={1.5} name="Отписались" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className={styles.chartEmpty}>Нет данных — запустите синк</div>}
        </div>

        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Охват по дням</span>
            <span className={styles.chartHint}>уникальные</span>
          </div>
          {dailyStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dailyStats} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tickFormatter={d => d.slice(8)} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis width={36} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }} />
                <Line type="monotone" dataKey="reach" stroke="#6366f1" dot={false} strokeWidth={2} name="Охват" />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className={styles.chartEmpty}>Нет данных</div>}
        </div>
      </div>

      {/* Charts row 2 */}
      <div className={styles.chartsRow2}>
        <div className={styles.chartCardWide}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Реакции по дням</span>
            <span className={styles.chartHint}>лайки — левая ось · репосты/комм. — правая</span>
          </div>
          {dailyStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dailyStats} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tickFormatter={d => d.slice(8)} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis yAxisId="left" width={28} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis yAxisId="right" width={24} orientation="right" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="left" type="monotone" dataKey="likes" stroke="#6366f1" dot={false} strokeWidth={2} name="Лайки" />
                <Line yAxisId="right" type="monotone" dataKey="reposts" stroke="#22c55e" dot={false} strokeWidth={1.5} name="Репосты" />
                <Line yAxisId="right" type="monotone" dataKey="comments" stroke="#f59e0b" dot={false} strokeWidth={1.5} name="Комментарии" />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className={styles.chartEmpty}>Нет данных</div>}
        </div>

        <div className={styles.chartCardNarrow}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Посты по рубрикам</span>
            <span className={styles.chartHint}>% от кол-ва</span>
          </div>
          {catCounts.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={catCounts} cx="50%" cy="45%" innerRadius={55} outerRadius={80}
                  dataKey="value" nameKey="name">
                  {catCounts.map((entry, i) => (
                    <Cell key={i} fill={entry.color || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className={styles.chartEmpty}>Нет рубрик</div>}
        </div>
      </div>

      {/* MoM Table — placed before Publications */}
      {momData && momData.months?.length > 0 && (
        <section className={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className={styles.sectionTitle}>Сравнение по месяцам</h2>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {momData.months.map((m) => m.label).join(' · ')}
            </span>
          </div>
          <div ref={momTableRef} className={`${styles.tableWrapper} ${styles.momTableWrapper}`}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ minWidth: 200 }}>Метрика</th>
                  {momData.months.map((m) => <th key={`${m.year}-${m.month}`}>{m.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {momData.rows.map((row) => (
                  <tr key={row.label}>
                    <td className={styles.momMetric}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {row.label}
                        {MOM_TOOLTIPS[row.label] && (
                          <MetricTooltip
                            description={MOM_TOOLTIPS[row.label].desc}
                            source={MOM_TOOLTIPS[row.label].src}
                          />
                        )}
                      </span>
                    </td>
                    {row.values.map((v, i) => {
                      const prev = i > 0 ? row.values[i - 1] : null
                      const delta = calcDelta(v, prev)
                      const fv = v !== null && v !== undefined
                        ? (row.fmt === 'percent' ? `${v}%` : row.fmt === 'full' ? formatFull(v) : formatNumber(v))
                        : '—'
                      return (
                        <td key={i} className={styles.momCell}>
                          <span className={styles.momValue}>{fv}</span>
                          {delta !== null && (
                            <span className={`${styles.momDelta} ${
                              delta === 0 ? styles.deltaZero
                              : (delta > 0) !== row.inverse ? styles.deltaPos : styles.deltaNeg
                            }`}>
                              {delta > 0 ? '↑' : '↓'}{Math.abs(delta)}%
                            </span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Posts table */}
      <section className={styles.section}>
        <div className={styles.tableControls}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <h2 className={styles.sectionTitle}>Публикации</h2>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {postsExpanded ? postsTotal : Math.min(postsTotal, 10)} из {postsTotal}
            </span>
            {!postsExpanded && postsTotal > 10 && (
              <button className="btn btn-ghost btn-sm" onClick={() => setPostsExpanded(true)}
                style={{ fontSize: 12 }}>
                Показать все →
              </button>
            )}
            {postsExpanded && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setPostsExpanded(false); setPostsPage(1) }}
                style={{ fontSize: 12 }}>
                Свернуть ↑
              </button>
            )}
          </div>
          <div className={styles.filters}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Формат:</span>
            {FORMAT_OPTIONS.map(f => (
              <button key={f}
                className={`btn btn-sm ${postsFormat === f ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setPostsFormat(f)}
                style={{ fontSize: 12, padding: '3px 10px' }}>
                {f === 'Все' ? 'Все' : FORMAT_LABELS[f]}
              </button>
            ))}
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>Рубрика:</span>
            <select className="form-input" value={postsCategoryId ?? ''}
              onChange={e => setPostsCategoryId(e.target.value ? Number(e.target.value) : null)}
              style={{ fontSize: 12, padding: '3px 8px', height: 28, width: 'auto' }}>
              <option value="">Все рубрики</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <SortTh col="published_at" label="Пост" current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                <th>Формат</th>
                <th>Рубрика</th>
                <SortTh col="views_1" label="Просмотры" current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                <SortTh col="likes" label="Лайки" current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                <th>Комм.</th>
                <SortTh col="reposts" label="Репосты" current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                <SortTh col="er" label={<>ER <MetricTooltip description="(лайки+комм+реп) / подписчики × 100" source="Расчёт" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                <SortTh col="err" label={<>ERR <MetricTooltip description="(лайки+комм+реп) / охват × 100" source="Расчёт" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
                <SortTh col="erviews" label={<>ERV <MetricTooltip description="(лайки+комм+реп) / просмотры × 100" source="Расчёт" /></>} current={postsSortBy} dir={postsSortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {posts.map(post => {
                const m = post.latest_metrics
                const cat = categories.find(c => c.id === post.category_id)
                return (
                  <tr key={post.id}>
                    <td className={styles.postCell}>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block' }}>{formatDate(post.published_at)}</span>
                      {post.post_url ? (
                        <a href={post.post_url} target="_blank" rel="noopener noreferrer" className={styles.postLink}>
                          {post.text_preview || '(без текста)'}
                        </a>
                      ) : <span>{post.text_preview || '(без текста)'}</span>}
                    </td>
                    <td>
                      {post.format !== 'text' ? (
                        <span className={styles.badge} style={{ background: FORMAT_COLORS[post.format] || '#475569' }}>
                          {FORMAT_LABELS[post.format] || post.format}
                        </span>
                      ) : (
                        <span className={styles.badgeGhost}>Текст</span>
                      )}
                    </td>
                    <td>
                      <select
                        disabled={!canManage}
                        value={post.category_id ?? ''}
                        onChange={e => handleCategoryChange(post.id, e.target.value ? Number(e.target.value) : null)}
                        className={styles.catSelect}
                        style={cat ? { borderColor: cat.color, color: cat.color } : undefined}
                      >
                        <option value="">—</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td>{m ? formatNumber(m.views_1) : '—'}</td>
                    <td>{m ? formatNumber(m.likes) : '—'}</td>
                    <td>{m ? formatNumber(m.comments) : '—'}</td>
                    <td>{m ? formatNumber(m.reposts) : '—'}</td>
                    <td className={m ? erClass(m.er) : ''}>{m ? `${m.er}%` : '—'}</td>
                    <td className={m ? erClass(m.err) : ''}>{m ? `${m.err}%` : '—'}</td>
                    <td className={m ? erClass(m.erviews) : ''}>{m ? `${m.erviews}%` : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {postsExpanded && totalPages > 1 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 8 }}>
            <button className="btn btn-secondary btn-sm" disabled={postsPage === 1} onClick={() => setPostsPage(p => p - 1)}>←</button>
            <span style={{ color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
              {postsPage} / {totalPages}
            </span>
            <button className="btn btn-secondary btn-sm" disabled={postsPage >= totalPages} onClick={() => setPostsPage(p => p + 1)}>→</button>
          </div>
        )}
      </section>

      {posts.length === 0 && platform.is_active && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
          Посты появятся после первой синхронизации (в 03:00 МСК)
        </div>
      )}
    </div>
  )
}

function SortTh({ col, label, current, dir, onSort }) {
  const active = current === col
  return (
    <th onClick={() => onSort(col)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ opacity: active ? 1 : 0.7 }}>{label}</span>{' '}
      <span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (dir === 'desc' ? '↓' : '↑') : '↕'}</span>
    </th>
  )
}
